/**
 * OracleNewPage
 * Yeni açılım akışı - Upload, Intent, Depth tek sayfada
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import OracleShell from '../components/OracleShell';
import PhotoUploadCard from '../components/PhotoUploadCard';
import IntentGrid from '../components/IntentGrid';
import DepthSelector from '../components/DepthSelector';
import { useOracleReading, STEPS, INTENTS, DEPTHS } from '../hooks/useOracleReading';
import { getEntitlement } from '../services/oracleApi';

export default function OracleNewPage({ token, credits, onCreditsUpdate }) {
  const navigate = useNavigate();
  
  // Entitlement state
  const [entitlement, setEntitlement] = useState(null);
  const [isLoadingEntitlement, setIsLoadingEntitlement] = useState(false);
  const [entitlementError, setEntitlementError] = useState(null);
  
  // Admin test mode'u entitlement'tan al
  const adminTestMode = entitlement?.admin_test_mode || false;
  
  const oracle = useOracleReading(token, credits, onCreditsUpdate, adminTestMode);
  
  // Load entitlement when component mounts
  useEffect(() => {
    if (token && !entitlement && !isLoadingEntitlement) {
      loadEntitlement();
    }
  }, [token]);
  
  const loadEntitlement = async () => {
    setIsLoadingEntitlement(true);
    setEntitlementError(null);
    
    try {
      const data = await getEntitlement(token);
      setEntitlement(data);
    } catch (err) {
      console.error('Entitlement error:', err);
      setEntitlementError(err.message || 'Bağlantı hatası');
    } finally {
      setIsLoadingEntitlement(false);
    }
  };
  
  // Step transitions
  const goToIntent = () => {
    if (oracle.cupImage) {
      oracle.setStep(STEPS.INTENT);
      oracle.setError(null);
    } else {
      oracle.setError('Lütfen fincan fotoğrafı yükleyin');
    }
  };
  
  const goToDepth = () => {
    if (oracle.intent) {
      oracle.setStep(STEPS.DEPTH);
      oracle.setError(null);
    } else {
      oracle.setError('Lütfen bir niyet seçin');
    }
  };
  
  const handleStart = async () => {
    await oracle.startReading();
    
    // Eğer başarılı başladıysa reading sayfasına yönlendir
    if (oracle.readingId) {
      navigate(`/oracle/reading/${oracle.readingId}`);
    }
  };
  
  // Redirect to reading page when loading starts
  React.useEffect(() => {
    if (oracle.step === STEPS.LOADING && oracle.readingId) {
      navigate(`/oracle/reading/${oracle.readingId}`);
    }
  }, [oracle.step, oracle.readingId, navigate]);
  
  const handleBack = () => {
    if (oracle.step === STEPS.INTENT) {
      oracle.setStep(STEPS.UPLOAD);
    } else if (oracle.step === STEPS.DEPTH) {
      oracle.setStep(STEPS.INTENT);
    } else {
      navigate('/oracle');
    }
  };
  
  const getTitle = () => {
    switch (oracle.step) {
      case STEPS.UPLOAD: return 'Fotoğraflar';
      case STEPS.INTENT: return 'Niyet';
      case STEPS.DEPTH: return 'Açılım Paketi';
      default: return 'MysticAI Oracle';
    }
  };
  
  return (
    <OracleShell 
      showBack={true} 
      onBack={handleBack}
      credits={credits}
      title={getTitle()}
    >
      <div className="oracle-content">
        
        {/* Error */}
        {oracle.error && (
          <div style={{ 
            background: 'rgba(239, 68, 68, 0.1)', 
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: 12,
            padding: 12,
            marginBottom: 20,
            fontSize: 14,
            color: '#ef4444',
            textAlign: 'center'
          }}>
            {oracle.error}
          </div>
        )}
        
        {/* STEP 1: UPLOAD */}
        {oracle.step === STEPS.UPLOAD && (
          <>
            {/* Step indicator */}
            <div className="oracle-step-indicator">
              <div className="oracle-step-dot active" />
              <div className="oracle-step-dot" />
              <div className="oracle-step-dot" />
            </div>
            
            <h2 style={{ textAlign: 'center', marginBottom: 8, fontSize: 20 }}>
              Fincan Fotoğrafı
            </h2>
            <p style={{ textAlign: 'center', color: 'var(--oracle-text-muted)', marginBottom: 24, fontSize: 14 }}>
              Fincanınızı çevirdikten sonra fotoğrafını çekin
            </p>
            
            <PhotoUploadCard
              image={oracle.cupImage}
              onUpload={(file) => oracle.uploadPhoto(file, 'cup')}
              onRemove={() => oracle.removePhoto('cup')}
              title="Fincan Fotoğrafı"
              subtitle="Yukarıdan çekin"
              required
            />
            
            {/* Optional: Tabak */}
            {oracle.cupImage && (
              <div style={{ marginTop: 16 }}>
                <p style={{ fontSize: 13, color: 'var(--oracle-text-muted)', marginBottom: 8, textAlign: 'center' }}>
                  İsteğe bağlı: Tabak fotoğrafı
                </p>
                <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                  <div style={{ width: 120 }}>
                    <PhotoUploadCard
                      image={oracle.saucerImage}
                      onUpload={(file) => oracle.uploadPhoto(file, 'saucer')}
                      onRemove={() => oracle.removePhoto('saucer')}
                      title="Tabak"
                      subtitle=""
                    />
                  </div>
                  <div style={{ width: 120 }}>
                    <PhotoUploadCard
                      image={oracle.handleImage}
                      onUpload={(file) => oracle.uploadPhoto(file, 'handle')}
                      onRemove={() => oracle.removePhoto('handle')}
                      title="Kulp"
                      subtitle=""
                    />
                  </div>
                </div>
              </div>
            )}
            
            <button 
              className="oracle-btn-primary" 
              onClick={goToIntent}
              disabled={!oracle.cupImage}
              style={{ width: '100%', marginTop: 24 }}
              data-testid="next-btn"
            >
              Devam
            </button>
          </>
        )}
        
        {/* STEP 2: INTENT */}
        {oracle.step === STEPS.INTENT && (
          <>
            {/* Step indicator */}
            <div className="oracle-step-indicator">
              <div className="oracle-step-dot completed" />
              <div className="oracle-step-dot active" />
              <div className="oracle-step-dot" />
            </div>
            
            <h2 style={{ textAlign: 'center', marginBottom: 8, fontSize: 20 }}>
              Niyetin Ne?
            </h2>
            <p style={{ textAlign: 'center', color: 'var(--oracle-text-muted)', marginBottom: 24, fontSize: 14 }}>
              Açılımın odağını seç
            </p>
            
            <IntentGrid
              intents={INTENTS}
              selected={oracle.intent}
              onSelect={oracle.setIntent}
            />
            
            {/* Optional question */}
            <div style={{ marginBottom: 24 }}>
              <label style={{ fontSize: 14, color: 'var(--oracle-text-secondary)', display: 'block', marginBottom: 8 }}>
                Sormak istediğin bir şey var mı? (İsteğe bağlı)
              </label>
              <textarea
                value={oracle.question}
                onChange={(e) => oracle.setQuestion(e.target.value)}
                placeholder="Örn: Bu kişi beni gerçekten seviyor mu?"
                style={{
                  width: '100%',
                  background: 'var(--oracle-glass-bg)',
                  border: '1px solid var(--oracle-glass-border)',
                  borderRadius: 12,
                  padding: 12,
                  color: 'var(--oracle-text-primary)',
                  fontSize: 14,
                  resize: 'none',
                  minHeight: 80
                }}
              />
            </div>
            
            <button 
              className="oracle-btn-primary" 
              onClick={goToDepth}
              disabled={!oracle.intent}
              style={{ width: '100%' }}
              data-testid="next-btn"
            >
              Devam
            </button>
          </>
        )}
        
        {/* STEP 3: DEPTH */}
        {oracle.step === STEPS.DEPTH && (
          <>
            {/* Step indicator */}
            <div className="oracle-step-indicator">
              <div className="oracle-step-dot completed" />
              <div className="oracle-step-dot completed" />
              <div className="oracle-step-dot active" />
            </div>
            
            <h2 style={{ textAlign: 'center', marginBottom: 8, fontSize: 20 }}>
              Açılım Paketi
            </h2>
            <p style={{ textAlign: 'center', color: 'var(--oracle-text-muted)', marginBottom: 24, fontSize: 14 }}>
              Ne kadar derine inmek istiyorsun?
            </p>
            
            <DepthSelector
              depths={DEPTHS}
              selected={oracle.depth}
              onSelect={oracle.setDepth}
              userCredits={credits}
              entitlement={entitlement}
              isLoadingEntitlement={isLoadingEntitlement}
              entitlementError={entitlementError}
            />
            
            <button 
              className="oracle-btn-primary" 
              onClick={handleStart}
              disabled={
                oracle.isSubmitting || 
                isLoadingEntitlement ||
                entitlementError ||
                (!entitlement?.admin_test_mode && credits < DEPTHS.find(d => d.id === oracle.depth)?.credits)
              }
              style={{ width: '100%' }}
              data-testid="start-reading-btn"
            >
              {oracle.isSubmitting ? 'Başlatılıyor...' : 'Açılımı Başlat'}
            </button>
            
            <p style={{ textAlign: 'center', fontSize: 12, color: 'var(--oracle-text-muted)', marginTop: 12 }}>
              {entitlement?.admin_test_mode 
                ? 'Admin test modu - kredi düşülmeyecek' 
                : `${DEPTHS.find(d => d.id === oracle.depth)?.credits} kredi düşülecek`
              }
            </p>
          </>
        )}
        
      </div>
    </OracleShell>
  );
}
